/**
 * Multi-select on the wall (Part 05 Q6): a ⌘/Ctrl- or Shift-click adds or
 * removes a card, and Esc clears the selection. The ids live in the
 * selection store, so the focus toggle reads the same selection as on the
 * canvas.
 */
import {
  useEffect,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
} from "react";
import { isTextEntryTarget } from "@/lib/editable-target";
import { useSelectionStore } from "@/stores/selection-store";
import { foreignWallFrame } from "@/stores/wall-scope-store";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";

/** A click that selects the card instead of opening it. */
function isWallSelectionClick(event: {
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
}): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey;
}

/**
 * Add or remove a card. A selection starts with the clicked card alone: the
 * frame selected by the last focus shows no ring on the wall, so it does not
 * join. Another project's card never joins: the selection's actions work
 * through the current project.
 */
function toggleWallCardSelection(frameId: string): void {
  if (foreignWallFrame(frameId)) return;
  const selection = useSelectionStore.getState();
  if (selection.multiSelectedFrameIds.length === 0) {
    selection.setMulti([frameId]);
    return;
  }
  selection.toggleMulti(frameId);
}

/**
 * What a press on a wall card or a Pinned tile's handle does: a plain click
 * opens it, a ⌘/Ctrl- or Shift-click selects it, and a right-click, the
 * context-menu key or Shift-F10 opens its menu.
 */
export function wallCardPressHandlers(
  id: string,
  onOpen: (id: string) => void,
  onMenu: (id: string, anchor: ContextMenuAnchor) => void,
) {
  const openMenuAt = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    onMenu(id, { x: rect.left, y: rect.top });
  };
  return {
    onClick: (event: MouseEvent<HTMLElement>) => {
      if (isWallSelectionClick(event)) toggleWallCardSelection(id);
      else onOpen(id);
    },
    onContextMenu: (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      // A keyboard-opened context menu reports no pointer position.
      if (event.clientX === 0 && event.clientY === 0) {
        openMenuAt(event.currentTarget);
        return;
      }
      onMenu(id, { x: event.clientX, y: event.clientY });
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      if (
        event.key !== "ContextMenu" &&
        !(event.shiftKey && event.key === "F10")
      ) {
        return;
      }
      event.preventDefault();
      openMenuAt(event.currentTarget);
    },
  };
}

/** Esc clears the wall's selection while `enabled` (no menu, no focus). */
export function useWallSelectionEscape(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing) return;
      if (isTextEntryTarget(event.target)) return;
      const selection = useSelectionStore.getState();
      if (selection.multiSelectedFrameIds.length === 0) return;
      selection.clearMulti();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled]);
}
